/**
 * Utilization Settings Component
 * Allows configuration of organization-wide utilization targets and calculation methods
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { TrendingUp, Calculator, Info, Lightbulb } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { UtilizationSettings as UtilizationSettingsType } from '@/types/timesheetSettings';

interface UtilizationSettingsProps {
  settings: UtilizationSettingsType;
  onChange?: (settings: UtilizationSettingsType, isValid: boolean) => void;
  onSave?: (settings: UtilizationSettingsType) => void;
}

export const UtilizationSettings: React.FC<UtilizationSettingsProps> = ({
  settings,
  onChange,
  onSave
}) => {
  const [localSettings, setLocalSettings] = useState<UtilizationSettingsType>(settings);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      // Validate settings
      const isValid = 
        localSettings.targetUtilizationRate >= 0 &&
        localSettings.targetUtilizationRate <= 1 &&
        ['billable-total', 'billable-available', 'billable-expected'].includes(localSettings.calculationMethod);
      
      onChange(localSettings, isValid);
    }
  }, [localSettings, onChange]);

  // Update local state when prop changes (for reset functionality)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (field: keyof UtilizationSettingsType, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Convert rate to percentage for display
  const ratePercentage = Math.round(localSettings.targetUtilizationRate * 100);

  // Get calculation method display name
  const getMethodDisplayName = (method: string): string => {
    switch (method) {
      case 'billable-total':
        return 'Billable / Total Hours';
      case 'billable-available':
        return 'Billable / Available Hours';
      case 'billable-expected':
        return 'Billable / Expected Hours';
      default:
        return method;
    }
  };

  // Get calculation method description
  const getMethodDescription = (method: string): string => {
    switch (method) {
      case 'billable-total':
        return 'Billable hours divided by total hours worked (including non-billable time)';
      case 'billable-available':
        return 'Billable hours divided by available hours (excludes PTO and holidays)';
      case 'billable-expected':
        return 'Billable hours divided by expected hours from working pattern (good for part-time staff)';
      default:
        return '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Utilization & Performance</CardTitle>
        </div>
        <CardDescription>
          Configure target utilization rates and calculation methods
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <p className="text-sm">
              These settings determine how employee utilization is calculated and what targets 
              are used for performance tracking. Changes affect dashboard metrics and reports.
            </p>
          </AlertDescription>
        </Alert>

        {/* Target Utilization Rate */}
        <div className="space-y-4">
          <Label className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Target Utilization Rate
          </Label>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-primary">{ratePercentage}%</span>
              <span className="text-sm text-muted-foreground">
                {ratePercentage < 60 && 'Low target'}
                {ratePercentage >= 60 && ratePercentage < 75 && 'Moderate target'}
                {ratePercentage >= 75 && ratePercentage < 85 && 'Standard target'}
                {ratePercentage >= 85 && 'High target'}
              </span>
            </div>
            
            <Slider
              value={[localSettings.targetUtilizationRate * 100]}
              onValueChange={(value) => handleChange('targetUtilizationRate', value[0] / 100)}
              min={0}
              max={100}
              step={5}
              className="w-full"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
            <Lightbulb className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-900 dark:text-blue-100">
              <p className="font-medium mb-2">Typical targets by role:</p>
              <ul className="space-y-1 text-xs">
                <li>• <strong>Consultants & Contractors:</strong> 75-85% (high billable focus)</li>
                <li>• <strong>Project Managers:</strong> 60-70% (mix of billable and admin)</li>
                <li>• <strong>Technical Leads:</strong> 50-60% (mentoring and architecture)</li>
                <li>• <strong>Support Staff:</strong> 40-50% (primarily non-billable)</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>

        {/* Calculation Method */}
        <div className="space-y-2">
          <Label htmlFor="calculation-method" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Calculation Method
          </Label>
          
          <Select
            value={localSettings.calculationMethod}
            onValueChange={(value) => handleChange('calculationMethod', value as UtilizationSettingsType['calculationMethod'])}
          >
            <SelectTrigger id="calculation-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="billable-total">
                <div className="flex flex-col items-start">
                  <span className="font-medium">Billable / Total Hours</span>
                  <span className="text-xs text-muted-foreground">Standard calculation</span>
                </div>
              </SelectItem>
              <SelectItem value="billable-available">
                <div className="flex flex-col items-start">
                  <span className="font-medium">Billable / Available Hours</span>
                  <span className="text-xs text-muted-foreground">Excludes PTO & holidays</span>
                </div>
              </SelectItem>
              <SelectItem value="billable-expected">
                <div className="flex flex-col items-start">
                  <span className="font-medium">Billable / Expected Hours</span>
                  <span className="text-xs text-muted-foreground">Based on working pattern</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <p className="text-xs text-muted-foreground mt-2">
            {getMethodDescription(localSettings.calculationMethod)}
          </p>
        </div>

        {/* Method Comparison */}
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <p className="text-sm font-medium">How calculation methods differ:</p>
          
          <div className="space-y-2 text-xs">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">Billable / Total Hours</p>
                <p className="text-muted-foreground">
                  Best for: Organizations tracking all time. Penalizes non-billable work.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Example: 30 billable + 10 non-billable = 75% utilization
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">Billable / Available Hours</p>
                <p className="text-muted-foreground">
                  Best for: Fair assessment when employees take PTO. Excludes time off.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Example: 30 billable + 10 non-billable + 8 PTO = 75% utilization (PTO ignored)
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">Billable / Expected Hours</p>
                <p className="text-muted-foreground">
                  Best for: Part-time staff or flexible schedules. Uses working pattern as baseline.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Example: 30 billable hours vs 40 expected = 75% utilization
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-lg bg-muted space-y-2">
          <p className="text-sm font-medium">Current Configuration:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Target rate: <span className="font-medium text-foreground">{ratePercentage}%</span></li>
            <li>• Calculation: <span className="font-medium text-foreground">{getMethodDisplayName(localSettings.calculationMethod)}</span></li>
            <li>• Applied to: <span className="font-medium text-foreground">All employees and reports</span></li>
          </ul>
        </div>

        {/* Legacy support: Keep individual save button if onSave is provided directly */}
        {onSave && (
          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={() => onSave(localSettings)} className="flex-1">
              Save Changes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
